import { Button } from "@/components/ui/button";
import FieldForm, { FieldInput } from "@/components/schema/FieldForm";
import { Label } from "@/components/ui/label";
import { deepMerge } from "@/lib/schema";

const LEFT = "hsl(190 80% 45%)";
const RIGHT = "hsl(300 70% 50%)";
const STEPS = 28;

// Ring sector of one arm seen from above. x = lateral (m), z = depth ahead (m).
// Angle 0 = straight ahead, positive = outward from the body's middle.
function sectorPath(side, arc, half, X, Y) {
  if (!arc) return "";
  const sx = side === "Left" ? -half : half;
  const dir = side === "Left" ? -1 : 1;
  const pt = (r, deg) => {
    const a = (deg * Math.PI) / 180;
    return [sx + dir * r * Math.sin(a), r * Math.cos(a)];
  };
  const pts = [];
  for (let i = 0; i <= STEPS; i += 1) pts.push(pt(arc.MaxDepth, arc.MinAngle + ((arc.MaxAngle - arc.MinAngle) * i) / STEPS));
  for (let i = STEPS; i >= 0; i -= 1) pts.push(pt(arc.MinDepth, arc.MinAngle + ((arc.MaxAngle - arc.MinAngle) * i) / STEPS));
  return `M ${pts.map(([x, z]) => `${X(x).toFixed(1)} ${Y(z).toFixed(1)}`).join(" L ")} Z`;
}

function Preview({ ub }) {
  const half = Number(ub.ShoulderHalfWidth) || 0.18;
  const ok = (a) => a && ["MinDepth", "MaxDepth", "MinAngle", "MaxAngle"].every((k) => Number.isFinite(Number(a[k])));
  // top-down
  const TX = (x) => 190 + x * 190, TY = (z) => 250 - z * 190;
  // side view
  const SX = (d) => 30 + d * 190, SY = (h) => 130 - h * 190;
  const rect = (a, color, off, label) => ok(a) && Number.isFinite(Number(a.MinHeight)) && Number.isFinite(Number(a.MaxHeight)) ? (
    <g>
      <rect x={SX(a.MinDepth) + off} y={SY(a.MaxHeight)} width={Math.max(0, (a.MaxDepth - a.MinDepth) * 190)}
      height={Math.max(0, (a.MaxHeight - a.MinHeight) * 190)} fill={color} fillOpacity="0.28" stroke={color} strokeWidth="1.5" />
      <text x={SX(a.MinDepth) + off + 4} y={SY(a.MaxHeight) + 13 + (label === "Right" ? 14 : 0)} fontSize="11" fontWeight="600" fill={color}>{label}</text>
    </g>
  ) : null;
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="reach-preview">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Top view</div>
        <svg viewBox="0 0 380 270" className="w-full bg-slate-50 rounded-lg border border-slate-200">
          <line x1="0" y1={TY(Number(ub.UiDistance) || 0)} x2="380" y2={TY(Number(ub.UiDistance) || 0)} stroke="#94a3b8" strokeDasharray="4 3" />
          <text x="6" y={TY(Number(ub.UiDistance) || 0) - 4} fontSize="9" fill="#64748b">UI {ub.UiDistance} m</text>
          {ok(ub.Left) && <path d={sectorPath("Left", ub.Left, half, TX, TY)} fill={LEFT} fillOpacity="0.3" stroke={LEFT} strokeWidth="1.5" />}
          {ok(ub.Right) && <path d={sectorPath("Right", ub.Right, half, TX, TY)} fill={RIGHT} fillOpacity="0.3" stroke={RIGHT} strokeWidth="1.5" />}
          <circle cx={TX(-half)} cy={TY(0)} r="4" fill={LEFT} />
          <circle cx={TX(half)} cy={TY(0)} r="4" fill={RIGHT} />
          <line x1={TX(-half)} y1={TY(0)} x2={TX(half)} y2={TY(0)} stroke="#475569" strokeWidth="2" />
          <text x={TX(-half)} y={TY(0) + 18} textAnchor="middle" fontSize="12" fontWeight="600" fill={LEFT}>Left</text>
          <text x={TX(half)} y={TY(0) + 18} textAnchor="middle" fontSize="12" fontWeight="600" fill={RIGHT}>Right</text>
        </svg>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Side view (depth × height)</div>
        <svg viewBox="0 0 270 270" className="w-full bg-slate-50 rounded-lg border border-slate-200">
          <line x1="30" y1={SY(0)} x2="260" y2={SY(0)} stroke="#cbd5e1" />
          <line x1="30" y1="0" x2="30" y2="270" stroke="#475569" strokeWidth="2" />
          {rect(ub.Left, LEFT, 0, "Left")}
          {rect(ub.Right, RIGHT, 0, "Right")}
        </svg>
      </div>
    </div>
  );
}

// Upper Body "reach" panel: body presets, both arcs, copy/mirror, live preview.
export default function ReachPanel({ group, values, onChange, role, patientData }) {
  const ub = values || {};
  const fields = group.fields || [];
  const arcs = fields.filter((f) => f._arc);
  const scalars = fields.filter((f) => !f._arc);
  const fillField = fields.find((f) => f.fills);
  const profiles = (fillField && patientData?.[fillField.fills]) || {};
  const has = (side) => arcs.some((a) => a.key === side);

  const setBody = (body) => {
    // Prefill from the chosen preset; every value stays editable afterwards
    const { Arc, ...rest } = profiles[body] || {};
    let next = deepMerge({ ...ub, [fillField.key]: body }, rest);
    if (Arc) arcs.forEach((a) => { next = { ...next, [a.key]: deepMerge(next[a.key], Arc) }; });   // one preset arc fills every arm
    onChange(next);
  };
  const copyLeftToRight = () => onChange({ ...ub, Right: { ...ub.Left } });
  const mirror = () => onChange({ ...ub, Right: { ...ub.Left }, Left: { ...ub.Right } });

  return (
    <div className="space-y-6">
      <FieldForm fields={scalars.filter((f) => !f.fills)} values={ub} onChange={onChange} role={role} patientData={patientData} />
      {fillField && (
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">{fillField.label || fillField.key}</Label>
          <div className="mt-2 flex items-center gap-3">
            <FieldInput field={fillField} value={ub[fillField.key]} role={role} patientData={patientData} onChange={setBody} />
            <span className="text-xs text-slate-500">Choosing a body fills the shoulder and arc values below. You can still edit them.</span>
          </div>
        </div>
      )}
      {has("Left") && has("Right") && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={copyLeftToRight} data-testid="copy-left-right">Copy left to right</Button>
          <Button type="button" variant="outline" size="sm" onClick={mirror} data-testid="mirror-arms">Mirror (swap)</Button>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
        <FieldForm fields={arcs} values={ub} onChange={onChange} role={role} patientData={patientData} depth={1} />
        <Preview ub={ub} />
      </div>
    </div>
  );
}
